// Resource module — deployment lifecycle (/deployments). React-free.
// Post-envelope wire shapes: list → { deployments; total } (total = full
// org-scoped count, NOT capped by limit); detail/mutations → { deployment };
// DELETE → { deleted: true } (void for callers).
import { apiFetch } from '@/lib/api'
import type { Deployment, DeploymentTimeline } from '@/types/api'

/** Peeled shape of GET /deployments — the list page as served: rows + org total. */
export type DeploymentListPage = { deployments: Deployment[]; total: number }

// Key contract: [domain, orgId, leaf, ...params] — see OrgQueryKey in @/lib/queries.
export const deploymentsKeys = {
  all: (orgId: string | null) => ['deployments', orgId] as const,
  list: (orgId: string | null) => ['deployments', orgId, 'list'] as const,
  /** Per-state count probe — fetches ?state=<s>&limit=1 and reads only
   *  `total`. The 'stateTotal' element keeps it distinct from the list key
   *  (same prefix, different arity — no same-key cache collision) while
   *  staying UNDER the 'list' prefix so one invalidation refreshes both. */
  stateTotal: (state: string) => (orgId: string | null) => ['deployments', orgId, 'list', 'stateTotal', state] as const,
  meteredEligibility: (orgId: string | null) => ['deployments', orgId, 'metered-eligibility'] as const,
  recent: (orgId: string | null) => ['deployments', orgId, 'recent'] as const,
  palette: (orgId: string | null) => ['deployments', orgId, 'palette'] as const,
  detail: (deploymentId: string) => (orgId: string | null) => ['deployments', orgId, 'detail', deploymentId] as const,
  timeline: (deploymentId: string) => (orgId: string | null) => ['deployments', orgId, 'timeline', deploymentId] as const,
  serverlessPanel: (deploymentId: string | null) => (orgId: string | null) =>
    ['deployments', orgId, 'serverless', deploymentId] as const,
  // The tier table of a bought reserved package (ADR-0052) — keyed by the
  // deployment it prices, like every other per-deployment read.
  reservedPrice: (deploymentId: string) => (orgId: string | null) =>
    ['deployments', orgId, 'reserved-price', deploymentId] as const,
  // The RESERVED menu (ADR-0052 §#51) — what the platform can back for one
  // model version. Keyed by the version it describes: another version is
  // another menu, and the menu is what the declaration is drawn from.
  reservedOptions: (params: { modelId: string; versionId: string }) => (orgId: string | null) =>
    ['deployments', orgId, 'reserved-options', params.modelId, params.versionId] as const,
}

/** GET /deployments — the org-scoped list. `state` narrows the rows to one
 *  wire state (the per-state count probe asks for `state` + `limit: 1` and
 *  reads only `total`); `limit` caps the rows, never the total. */
export function fetchDeployments(
  orgId: string,
  params?: { state?: string; limit?: number | string },
): Promise<DeploymentListPage> {
  return apiFetch<DeploymentListPage>('/deployments', { orgId, params: { state: params?.state, limit: params?.limit } })
}

// GET /deployments/metered-eligibility — the ADR-0041 roster read. The
// console's deploy path is reserved-only (no metered create), so this gates
// the BILLING surface: the card-hour price rows stay hidden for non-operators
// instead of advertising a rate the tenant cannot buy.
export async function fetchMeteredEligibility(orgId: string): Promise<boolean> {
  return (await apiFetch<{ meteredCreateAllowed: boolean }>('/deployments/metered-eligibility', { orgId }))
    .meteredCreateAllowed
}

// GET /deployments/:id → { deployment }
export async function fetchDeployment(orgId: string, deploymentId: string): Promise<Deployment> {
  return (await apiFetch<{ deployment: Deployment }>(`/deployments/${deploymentId}`, { orgId })).deployment
}

// GET /deployments/:id/timeline — {success,data:{state,createdAt,updatedAt,
// failure?,events[]}}. The data payload IS the timeline — no wrapper key.
export function fetchDeploymentTimeline(orgId: string, deploymentId: string): Promise<DeploymentTimeline> {
  return apiFetch<DeploymentTimeline>(`/deployments/${deploymentId}/timeline`, { orgId })
}

// PATCH {action:'start'} — explicit bring-up: provisioning→loading→ready.
// The serverless deploy path uses it; the reserved purchase does NOT (the
// purchase starts the endpoint itself — 付款即启动).
export async function startDeployment(orgId: string, deploymentId: string): Promise<Deployment> {
  return (await apiFetch<{ deployment: Deployment }>(`/deployments/${deploymentId}`, {
    method: 'PATCH',
    orgId,
    body: JSON.stringify({ action: 'start' }),
  })).deployment
}
